<template>
  <section class="privacy-page">
    <div class="privacy-hero">
      <p class="eyebrow">Legal</p>
      <h1>{{ policyTitle }}</h1>
      <p>{{ modifiedDate }}</p>
    </div>

    <div class="privacy-layout">
      <aside class="policy-card" aria-label="Privacy policy sections">
        <p class="policy-card-label">Policy sections</p>
        <p class="policy-card-date">{{ modifiedDate }}</p>
        <nav class="policy-nav">
          <a
            v-for="section in navSections"
            :key="section.id"
            class="policy-nav-link"
            :class="{ active: activeSection === section.id }"
            :href="`#${section.id}`"
          >
            {{ section.navTitle }}
          </a>
        </nav>
      </aside>

      <article class="policy-content">
        <section
          v-for="section in contentSections"
          :id="section.id"
          :key="section.id"
          ref="sectionEls"
          class="policy-section"
        >
          <h2>{{ section.title }}</h2>
          <template v-for="block in section.blocks" :key="block.text || block.items?.join('|')">
            <ul v-if="block.type === 'list'" class="policy-list">
              <li v-for="item in block.items" :key="item">{{ item }}</li>
            </ul>
            <div v-else class="policy-point">
              <p>
                <strong v-if="block.prefix" class="policy-point-prefix">{{ block.prefix }}</strong>
                <template v-for="(segment, index) in block.segments" :key="`${segment.text}-${index}`">
                  <a v-if="segment.href" :href="segment.href" target="_blank" rel="noopener noreferrer">{{ segment.text }}</a>
                  <span v-else>{{ segment.text }}</span>
                </template>
              </p>
            </div>
          </template>
        </section>
      </article>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useHead } from '@vueuse/head'

useHead({
  title: 'Privacy Policy | NetFlyr',
  meta: [
    { name: 'description', content: 'Review the NetFlyr Privacy Policy to learn how we collect, use, protect, and share information related to accounts, flyer activity, locations, communications, cookies, and platform security.' },
    { name: 'keywords', content: 'NetFlyr privacy policy, data privacy, account information, location privacy, cookies, user data, platform security' }
  ]
})

const policyTitle = ref('Privacy Policy')
const modifiedDate = ref('Last modified: June 2026')
const contentSections = ref([])
const activeSection = ref('overview')
const sectionEls = ref([])
let observer = null

const navSections = computed(() =>
  contentSections.value.map((section) => ({
    id: section.id,
    title: section.title,
    navTitle: formatNavTitle(section.title)
  }))
)

const formatNavTitle = (value) => {
  const normalized = String(value || '')
    .replace(/^\d+\.\s*/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

  return normalized.replace(/\b[a-z]/g, (letter) => letter.toUpperCase())
}

const slugify = (value, fallback) => {
  const slug = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return slug || fallback
}

const linkifyText = (text) => {
  const value = String(text || '')
  const segments = []
  const urlPattern = /(https?:\/\/[^\s]+|netflyr\.com)/gi
  let lastIndex = 0
  let match

  while ((match = urlPattern.exec(value)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: value.slice(lastIndex, match.index) })
    }

    const urlText = match[0]
    segments.push({
      text: urlText,
      href: urlText.startsWith('http') ? urlText : `https://${urlText}`
    })
    lastIndex = match.index + urlText.length
  }

  if (lastIndex < value.length) {
    segments.push({ text: value.slice(lastIndex) })
  }

  return segments.length ? segments : [{ text: value }]
}

const pushTextBlock = (section, text) => {
  const value = String(text || '').trim()
  const pointMatch = value.match(/^(\d+(?:\.\d+)+\.?)\s*(.*)$/)
  const prefix = pointMatch?.[1] || ''
  const body = pointMatch ? pointMatch[2] : value
  if (value) section.blocks.push({ type: 'text', text: value, prefix, segments: linkifyText(body) })
}

const pushListItem = (section, text) => {
  const value = String(text || '').replace(/^•\s*/, '').trim()
  if (!value) return
  const lastBlock = section.blocks[section.blocks.length - 1]
  if (lastBlock?.type === 'list') {
    lastBlock.items.push(value)
    return
  }
  section.blocks.push({ type: 'list', items: [value] })
}

const createSection = (title, index) => ({
  id: slugify(title, `policy-section-${index}`),
  title,
  blocks: []
})

const parsePolicy = (rawText) => {
  const lines = String(rawText || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)

  policyTitle.value = lines.shift() || 'Privacy Policy'
  modifiedDate.value = lines.shift() || 'Last modified'

  const sections = [createSection('Overview', 0)]
  let current = sections[0]

  lines.forEach((line) => {
    const sectionMatch = line.match(/^(\d+)\.\s+(.+)$/)

    if (line.toUpperCase().startsWith('IN WITNESS WHEREOF')) {
      current = createSection('Acknowledgement', sections.length)
      sections.push(current)
      pushTextBlock(current, line)
      return
    }

    if (sectionMatch && !line.match(/^\d+\.\d+/)) {
      current = createSection(line, sections.length)
      sections.push(current)
      return
    }

    if (line.startsWith('•')) {
      pushListItem(current, line)
      return
    }

    pushTextBlock(current, line)
  })

  contentSections.value = sections.filter((section) => section.blocks.length)
}

const observeSections = async () => {
  await nextTick()
  observer?.disconnect()
  observer = new IntersectionObserver(
    (entries) => {
      const visibleEntry = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]

      if (visibleEntry?.target?.id) {
        activeSection.value = visibleEntry.target.id
      }
    },
    {
      rootMargin: '-25% 0px -55% 0px',
      threshold: [0.12, 0.35, 0.6]
    }
  )

  sectionEls.value.forEach((section) => {
    if (section?.id) observer.observe(section)
  })
}

const loadPolicy = async () => {
  try {
    const response = await fetch('/legal/privacy-policy.txt')
    const text = await response.text()
    parsePolicy(text)
    await observeSections()
  } catch {
    contentSections.value = [
      {
        id: 'overview',
        title: 'Overview',
        blocks: [{ type: 'text', text: 'Privacy policy content could not be loaded.' }]
      }
    ]
  }
}

onMounted(loadPolicy)

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<style scoped>
.privacy-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.1), transparent 34rem),
    linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%);
  padding: 2rem clamp(1rem, 4vw, 3rem) 4rem;
}

.privacy-hero {
  margin: 0 auto 1.25rem;
  max-width: 72rem;
}

.eyebrow {
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgb(37, 99, 235);
}

.privacy-hero h1 {
  margin: 0;
  color: rgb(15, 23, 42);
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 650;
  line-height: 1;
}

.privacy-hero p:last-child {
  margin: 0.75rem 0 0;
  color: rgb(71, 85, 105);
  font-size: 1rem;
  font-weight: 400;
}

.privacy-layout {
  display: grid;
  grid-template-columns: minmax(15rem, 19rem) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
  max-width: 72rem;
  margin: 0 auto;
}

.policy-card {
  position: sticky;
  top: 5.5rem;
  max-height: calc(100vh - 7rem);
  overflow: auto;
  border: 1px solid rgba(191, 219, 254, 0.9);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.92);
  padding: 1rem;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
}

.policy-card-label {
  margin: 0;
  color: rgb(15, 23, 42);
  font-size: 0.95rem;
  font-weight: 600;
}

.policy-card-date {
  margin: 0.25rem 0 0.85rem;
  color: rgb(100, 116, 139);
  font-size: 0.8rem;
  font-weight: 400;
}

.policy-nav {
  display: grid;
  gap: 0.35rem;
}

.policy-nav-link {
  display: block;
  border-radius: 0.75rem;
  padding: 0.6rem 0.7rem;
  color: rgb(71, 85, 105);
  font-size: 0.88rem;
  font-weight: 400;
  line-height: 1.3;
  text-decoration: none;
  transition: background 0.16s ease, color 0.16s ease;
}

.policy-nav-link:hover,
.policy-nav-link.active {
  background: rgb(219, 234, 254);
  color: rgb(29, 78, 216);
  font-weight: 500;
}

.policy-content {
  display: grid;
  gap: 1rem;
}

.policy-section {
  scroll-margin-top: 6rem;
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 1.35rem;
  background: rgba(255, 255, 255, 0.96);
  padding: clamp(1.1rem, 3vw, 1.75rem);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
}

.policy-section h2 {
  margin: 0 0 0.9rem;
  color: rgb(15, 23, 42);
  font-size: clamp(1.08rem, 2vw, 1.45rem);
  font-weight: 600;
  line-height: 1.25;
}

.policy-section p {
  margin: 0;
  color: rgb(51, 65, 85);
  font-size: 0.96rem;
  font-weight: 400;
  line-height: 1.72;
}

.policy-point {
  border-top: 1px solid rgb(241, 245, 249);
  padding-top: 0.8rem;
  margin-top: 0.8rem;
}

.policy-point-prefix {
  margin-right: 0.45rem;
  color: rgb(30, 64, 175);
  font-weight: 700;
}

.policy-section a {
  color: rgb(37, 99, 235);
  font-weight: 500;
  text-decoration: none;
}

.policy-section a:hover {
  text-decoration: underline;
}

.policy-list {
  margin: 0.85rem 0 0;
  padding-left: 1.2rem;
  color: rgb(51, 65, 85);
  font-size: 0.96rem;
  font-weight: 400;
  line-height: 1.7;
}

.policy-list li + li {
  margin-top: 0.4rem;
}

@media (max-width: 900px) {
  .privacy-layout {
    grid-template-columns: 1fr;
  }

  .policy-card {
    position: sticky;
    top: 4.75rem;
    z-index: 5;
    max-height: 16rem;
  }
}
</style>
